// Resource module — acquisition surfaces (/acquisition). React-free.
// ADR-0042 deposit-gated programmes: POST /acquisition/referral-bind binds
// this org to the inviter org its invite code names (once, ever — the
// partial-unique index is the authority) and GET /acquisition/referral reads
// the org's referral state (bound inviter, own reward row, inviter earnings).
// The 200 bind body is the DECISION, not an error path: bound=false with
// reason existing is the recorded no-op, self_referral/unknown_inviter are
// recorded refusals — never a 5xx.
import { apiFetch } from '@/lib/api'
import type { AcquisitionChannel } from '@/lib/acquisition'
import type { paths } from '@/types/openapi'

type ReferralBindWire = paths['/api/acquisition/referral-bind']['post']['responses'][200]['content']['application/json']

/** Peeled decision payload — {success,data:{…}} loses both envelope layers. */
export type ReferralBindDecision = ReferralBindWire['data']

// The channel list is hand-kept in @/lib/acquisition (the generated contract is
// types-only), so pin it against the wire enum: tsc fails the moment the
// backend's enum moves and the two drift apart.
type WireChannel = NonNullable<
  NonNullable<paths['/api/acquisition/referral-bind']['post']['requestBody']>['content']['application/json']['channel']
>
type AssertEqual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never
const CHANNEL_PARITY: AssertEqual<AcquisitionChannel, WireChannel> = true
void CHANNEL_PARITY

export function bindReferral(
  orgId: string,
  input: { code: string; channel: AcquisitionChannel; source: string | null },
): Promise<ReferralBindDecision> {
  return apiFetch<ReferralBindDecision>('/acquisition/referral-bind', {
    method: 'POST',
    orgId,
    body: JSON.stringify(input.source ? input : { code: input.code, channel: input.channel }),
  })
}

type ReferralStateWire = paths['/api/acquisition/referral']['get']['responses'][200]['content']['application/json']

/** Peeled referral state — {success,data:{…}} loses both envelope layers. */
export type ReferralState = ReferralStateWire['data']

export function fetchReferralState(orgId: string): Promise<ReferralState> {
  return apiFetch<ReferralState>('/acquisition/referral', { orgId })
}

// Key contract: [domain, orgId, leaf, ...params] — see OrgQueryKey in @/lib/queries.
export const acquisitionKeys = {
  all: (orgId: string | null) => ['acquisition', orgId] as const,
  referral: (orgId: string | null) => ['acquisition', orgId, 'referral'] as const,
}
